











// Composition
import {
  defineComponent,
  useMeta,
  useRoute,
  watch,
  useContext,
  useRouter,
  useFetch,
  onMounted,
} from '@nuxtjs/composition-api'

// Common
import { noneFilterArray } from '@/common/static/facets'
import { decodeHtml } from '@/common/utils/characters'

// Store
import { useCompareProductsStore } from '@/store/compare-products'
import { useSearchStore } from '@/store/search'

// Composable
import useSearch from '@/composables/search/useSearch'
import useSearchUrl from '@/composables/search/useSearchUrl'
import useSearchAnalytics from '@/composables/analytics/useSearchAnalytics'

// Components
import Errors from '@/components/global/errors/Errors.vue'
import Results from '@/components/feature/search/results/Results.vue'
import ComparePanel from '@/components/feature/search/compare/ComparePanel.vue'
import ProductAddToCartModal from '@/components/global/modals/ProductAddToCartModal.vue'

export default defineComponent({
  name: 'Browse',

  components: {
    Errors,
    Results,
    ComparePanel,
    ProductAddToCartModal,
  },

  setup() {
    const { trackSearchResults, trackProductClick, trackElasticsearchClickEvent } = useSearchAnalytics()
    const searchStore = useSearchStore()

    const { searchErrors, performSearch, performAlternativeSearch, storeCachedFacets } = useSearch()
    const { getSearchUrl, setSearchUrl } = useSearchUrl()
    const route = useRoute()
    const comparedProductsStore = useCompareProductsStore()
    const router = useRouter()
    const context = useContext()

    async function redirectToPDP(response: any) {
      trackProductClick(response)
      await trackElasticsearchClickEvent(response.productnumber.raw)

      if (process.server) {
        // on the server `context.redirect` send 302 status code, but on the client it uses client side router,
        // therefore to perform full page reload on the client we have to use `window.location.href`
        context.redirect(response.clickurl.raw)
        return
      }
      window.location.href = response.clickurl.raw
    }
    async function showSearchResults() {
      // check for compare IDs in URL
      // If present, set them in the compare-product store
      // TODO delete after sunset ATG
      await comparedProductsStore.setCompareProductsFromSession()
    }

    const alternateSearch = async () => {
      if (!process.client) return

      // search the alternative tab (the one that is currently not selected - search other tab)
      const tabToRedirect = await performAlternativeSearch()
      if (tabToRedirect) {
        searchStore.setSelectedTab(tabToRedirect)
        router.push(getSearchUrl())
        await performSearch()
      }
    }

    const search = async () => {
      // check for URL parameters
      // do this on every route change as long as we have ATG pages. They redirect to the /browse page with URL parameters
      setSearchUrl(route.value.query)

      const response = await performSearch()

      // Track results (only happening for client side searches. Server side request are handled on onMounted)
      trackSearchResults({
        searchTerm: searchStore.searchInput,
        searchType: searchStore.getSearchType,
        searchResults: searchStore.getTotalResults,
        searchResponseTime: searchStore.page.responseTime,
        searchFilters: searchStore.facets.checkedFacets,
      })

      // if the response has a prop directproductnumber
      // And the categories is an array instead of empty
      // ** this category check is for the STEP team. They publish products without a category to hide them in the website
      // redirect the user immediately
      if (
        response?.directproductnumber !== undefined &&
        response.categories?.raw &&
        Array.isArray(response.categories.raw) &&
        !noneFilterArray[0].categories.includes(response.categories.raw[0])
      ) {
        await redirectToPDP(response)
        return
      }

      await showSearchResults()
    }

    useFetch(async () => {
      await storeCachedFacets() // Get all Facets on page load
      await search() // Perform a server side search
    })

    onMounted(async () => {
      // Track results on page load
      // Results in the useFetch search are not tracked because this is happening searver side
      if (searchStore.searchInput || searchStore.facets.checkedFacets?.length) {
        trackSearchResults({
          searchTerm: searchStore.searchInput,
          searchType: searchStore.getSearchType,
          searchResults: searchStore.getTotalResults,
          searchResponseTime: searchStore.page.responseTime,
          searchFilters: searchStore.facets.checkedFacets,
        })
      }
      await search()
      alternateSearch()
    })

    // Listen to a route change
    watch(route, async () => {
      // Scoll page to the top of noscroll does not appear in url
      // this is only set when a user clicks on a facet
      if (process.client && !Object.keys(route.value.query).includes('noscroll')) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }

      await search()
      alternateSearch()
    })
    useMeta(() => {
      const isResourceTab = searchStore.isResourcesTab
      const twoFacetGroup = searchStore.facets.checkedFacets.some((facet) => {
        return Object.values(facet)[0]?.length >= 2
      })
      const atLeastThreeFacetGroups = searchStore.facets.checkedFacets.length >= 3

      const isNewProductFacet = searchStore.facets.checkedFacets.some((facet) => {
        return Object.keys(facet)[0] === 'newproduct'
      })
      const isAnyOfFollowingFacet = searchStore.facets.checkedFacets.some((facet) => {
        return (
          Object.keys(facet)[0] === 'nr' ||
          Object.keys(facet)[0] === 'search' ||
          Object.keys(facet)[0] === 'status' ||
          Object.keys(facet)[0] === 'redirect' ||
          Object.keys(facet)[0] === 'noscroll' ||
          Object.keys(facet)[0] === 'sortby'
        )
      })

      let newMetaTitle: string

      // Iterate over checked facet groups and get a name of each checked facet
      const checkedFacets = Object.values(searchStore.facets.checkedFacets).flatMap((facet) => {
        const facetValues = Object.values(facet)[0] as string[] | ['true']

        // Facets from the `other` group have a different structure.
        // To get their name we need to use a key of the `facet` object instead of its value.
        if (facetValues[0] === 'true') {
          const facetName = Object.keys(facet)[0]
          return [facetName.charAt(0).toUpperCase() + facetName.slice(1)] // Capitalize facet name
        }

        return facetValues
      })
      if (checkedFacets.length > 0 && checkedFacets.length < 3) {
        newMetaTitle = checkedFacets
          .map((el) => decodeHtml(el))
          .reverse()
          .join(' ')
      } else {
        newMetaTitle = 'All Categories: Antibodies, ELISA Kits, Lysates'
      }

      const meta: any[] = [
        {
          hid: 'Visit CellSignal.com to view our All Categories materials including Antibodies, ELISA Kits, Lysates & more. CST - Customer satisfaction is our highest priority.',
          name: 'All Categories: Antibodies, ELISA Kits, Lysates | Cell Signaling Technology',
          content:
            'Visit CellSignal.com to view our All Categories materials including Antibodies, ELISA Kits, Lysates & more. CST - Customer satisfaction is our highest priority.',
        },
      ]
      const link: any[] = []

      if (isResourceTab || twoFacetGroup || atLeastThreeFacetGroups || isAnyOfFollowingFacet) {
        if (!isNewProductFacet) {
          meta.push({
            name: 'robots',
            content: 'noindex',
          })
        }
        link.push({
          rel: 'canonical',
          href: context.$config.cst.domainDefault + route.value.path,
        })
      } else {
        link.push({
          rel: 'canonical',
          href: route.value.fullPath,
        })
      }

      return {
        title: newMetaTitle,
        meta,
        link,
      }
    })

    return {
      searchErrors,
    }
  },

  head: {},
})
